import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus controller for Tasks Beta saved views
 * Handles capturing current filter/sort/group state when saving views
 */
export default class extends Controller {
  static targets = ["filtersForm", "sortForm", "groupingForm", "columnsForm"]
  static values = {
    activeSearchId: String,
    activeSearchName: String
  }

  connect() {
    console.log('Tasks Beta saved views controller connected')
    this.restoreFiltersCollapseState()

    // Also restore after turbo frame loads
    document.addEventListener('turbo:frame-load', this.boundRestoreState = () => {
      this.restoreFiltersCollapseState()
    })
  }

  disconnect() {
    if (this.boundRestoreState) {
      document.removeEventListener('turbo:frame-load', this.boundRestoreState)
    }
  }

  // Quick update - saves current params to existing view without showing modal
  quickUpdate(event) {
    event.preventDefault()

    const button = event.currentTarget
    const url = button.dataset.url
    const searchName = button.dataset.searchName
    const searchVersion = button.dataset.searchVersion

    if (!url) {
      console.error('No URL provided for quick update')
      return
    }

    button.disabled = true

    const searchParams = this.getCurrentParams()

    const data = {
      saved_form_view: {
        name: searchName,
        version: searchVersion,
        context: 'tasks_beta',
        search_params: searchParams
      }
    }

    fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html'
      },
      body: JSON.stringify(data)
    })
    .then(response => {
      if (response.ok) {
        return response.text()
      } else {
        throw new Error('Update failed')
      }
    })
    .then(html => {
      Turbo.renderStreamMessage(html)

      // Hide the "Undo Changes" link
      const revertLink = button.parentElement.querySelector('a[href*="revert=true"]')
      if (revertLink) {
        revertLink.style.display = 'none'
      }

      // Update URL to clean state (only view_id)
      const currentUrl = new URL(window.location.href)
      const cleanUrl = new URL(currentUrl.origin + currentUrl.pathname)
      cleanUrl.searchParams.set('view_id', button.dataset.searchId)
      window.history.replaceState({}, '', cleanUrl.toString())
    })
    .catch(error => {
      console.error('Update error:', error)
      button.disabled = false
    })
  }

  // Save the collapse state when toggling filters
  saveFiltersCollapseState() {
    const collapseEl = this.element.querySelector('#selectedFilters')
    if (collapseEl) {
      const isExpanded = collapseEl.classList.contains('show')
      sessionStorage.setItem('beta_filters_expanded', isExpanded ? 'true' : 'false')
    }
  }

  // Restore the collapse state after page reload/navigation
  restoreFiltersCollapseState() {
    const shouldExpand = sessionStorage.getItem('beta_filters_expanded') === 'true'
    if (shouldExpand) {
      const collapseEl = this.element.querySelector('#selectedFilters')
      const toggleEl = this.element.querySelector('#selectedFiltersToggle')
      if (collapseEl && !collapseEl.classList.contains('show')) {
        collapseEl.classList.add('show')
        if (toggleEl) {
          toggleEl.setAttribute('aria-expanded', 'true')
        }
      }
    }
  }

  // Called when clicking a filter badge to remember state before navigation
  rememberFiltersExpanded(event) {
    sessionStorage.setItem('beta_filters_expanded', 'true')
  }

  handleRevert(event) {
    event.preventDefault()

    const undoLink = event.currentTarget
    const undoUrl = undoLink.href

    // Immediately disable the "Update view" button
    const updateButton = this.element.querySelector('[data-save-view-button]')
    if (updateButton) {
      updateButton.disabled = true
    }

    // Immediately hide the "Undo Changes" link
    if (undoLink) {
      undoLink.style.display = 'none'
    }

    // Fetch the undone content first (with revert=true)
    fetch(undoUrl, {
      headers: {
        'Accept': 'text/html',
        'Turbo-Frame': 'beta_content'
      }
    })
    .then(response => response.text())
    .then(html => {
      // Parse the response and extract the frame content
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const newFrameContent = doc.querySelector('#beta_content')

      if (newFrameContent) {
        const currentFrame = document.querySelector('#beta_content')
        if (currentFrame) {
          currentFrame.innerHTML = newFrameContent.innerHTML
        }
      }

      // After updating content, update the URL to clean state (without revert param)
      const cleanUrl = new URL(undoUrl)
      cleanUrl.searchParams.delete('revert')
      window.history.replaceState({}, '', cleanUrl.toString())
    })
    .catch(error => {
      console.error('Revert error:', error)
      // Fall back to navigation if fetch fails
      Turbo.visit(undoUrl, { frame: 'beta_content' })
    })
  }

  // Collect current params from all dropdowns
  getCurrentParams() {
    const params = {}

    // Get params from filters form
    if (this.hasFiltersFormTarget) {
      const filtersData = new FormData(this.filtersFormTarget)
      for (let [key, value] of filtersData.entries()) {
        if (this.shouldIncludeParam(key)) {
          this.addParam(params, key, value)
        }
      }
    }

    // Get params from sort form
    if (this.hasSortFormTarget) {
      const sortData = new FormData(this.sortFormTarget)
      for (let [key, value] of sortData.entries()) {
        if (this.shouldIncludeParam(key)) {
          this.addParam(params, key, value)
        }
      }
    }

    // Get params from grouping form
    if (this.hasGroupingFormTarget) {
      const groupingData = new FormData(this.groupingFormTarget)
      for (let [key, value] of groupingData.entries()) {
        if (this.shouldIncludeParam(key)) {
          this.addParam(params, key, value)
        }
      }
    }

    // Get params from columns form
    if (this.hasColumnsFormTarget) {
      const columnsData = new FormData(this.columnsFormTarget)
      for (let [key, value] of columnsData.entries()) {
        if (this.shouldIncludeParam(key)) {
          this.addParam(params, key, value)
        }
      }
    }

    console.log('Collected params:', params)
    return params
  }

  addParam(params, key, value) {
    // Handle array params (like status[], account_id[], etc.)
    if (key.endsWith('[]')) {
      const cleanKey = key.replace('[]', '')
      if (!params[cleanKey]) {
        params[cleanKey] = []
      }
      // Only add if not already present (avoid duplicates from hidden fields in multiple forms)
      if (!params[cleanKey].includes(value)) {
        params[cleanKey].push(value)
      }
    } else {
      // Handle single value params - take the last value (most recent form submission)
      params[key] = value
    }
  }

  shouldIncludeParam(key) {
    // Exclude Rails internal params and view_id (since that's metadata, not search params)
    const excludedFields = ['authenticity_token', 'utf8', 'commit', 'controller', 'action', 'view_id']
    return !excludedFields.includes(key) && !excludedFields.includes(key.replace('[]', ''))
  }
}
